import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
} from "react-table";
import Swal from "sweetalert2";
import { FaSearch, FaFileDownload } from "react-icons/fa";
import { HiRefresh } from "react-icons/hi";
import Loader from "../../component/Loader";
import "../../css/makeTable.css";
import { getData } from "../../store/utils";
import DatePicker from "react-datepicker";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { useDebounce } from 'use-debounce';
import Select from "react-select";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    overflow: "none",
    maxWidth:"95%",
  },
};

const ItemInnerDetails = () => {
  const inputRef = useRef();
  const [isLoadingOn, setIsLoadingOn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [records, setRecords] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState(""); 
  const [orderBy, setOrderBy] = useState("");
  const [searchBoxValue, setSearchBoxValue] = useState("");
  const [queryParams, setQueryParams] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [todayDate, setTodayDate] = useState(null);
  const [filterData, setFilterData] = useState('');
  const [value] = useDebounce(searchBoxValue, 1000);
  
  const statusOption = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'currentWeek', label: 'Current Week' },    
    { value: 'lastWeek', label: 'Last Week' }, 
    { value: 'currentMonth', label: 'Current Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'overall', label: 'Overall' }, 
  ];

  const getItemData = async () => {
    if (isLoadingOn) return;
    setIsLoadingOn(true);
    const today = format(new Date(), "yyyy-MM-dd");
    setTodayDate(today);
    
    let computedFromDate = fromDate;
    let computedToDate = toDate;
    
    switch (filterData) {
        case "today":
            computedFromDate = today;
            computedToDate = today;
            break;
        case "yesterday":
            computedFromDate = format(subDays(new Date(), 1), "yyyy-MM-dd");
            computedToDate = computedFromDate;
            break;
        case "currentWeek":
            computedFromDate = format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
            computedToDate = format(endOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
            break;
        case "lastWeek":
            computedFromDate = format(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
            computedToDate = format(endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
            break;
        case "currentMonth":
            computedFromDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
            computedToDate = format(endOfMonth(new Date()), "yyyy-MM-dd");
            break;
        case "lastMonth":
            computedFromDate = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
            computedToDate = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
            break;
        case "overall":
            computedFromDate = null;
            computedToDate = null;
            break;
    }
    
    let queryParams = new URLSearchParams();
    
    queryParams.append("page", (pageIndex + 1).toString());
    queryParams.append("pageSize", pageSize.toString());
    
    if (computedFromDate) queryParams.append("fromDate", computedFromDate);
    if (computedToDate) queryParams.append("toDate", computedToDate);
    
    if (sortBy && orderBy) {
        queryParams.append("sortBy", sortBy);
        queryParams.append("orderBy", orderBy);
    }
    
    if (searchBoxValue.trim()) {
        queryParams.append("search", searchBoxValue.trim());
    }

    const apiUrl = `dashboard/productSummaryData?${queryParams.toString()}`;

    try {
      const getDataResult = await getData(apiUrl);
      if (getDataResult.statusCode == 200) {
        setRecords(getDataResult.data.records);
        setPageCount(getDataResult.data.pagination.totalPagesCount);
        setTotalRecords(getDataResult.data.pagination.totalRecords);

        if (getDataResult?.data?.pagination?.totalRecords == 0) {
          setErrorMessage("No data found");
        } else {
          setErrorMessage("");
        }
      }
      else {
        Swal.fire({
          title: "Error!",
          text: getDataResult?.response?.data?.errors?.msg || "Something went wrong."
        })
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "An error occurred while fetching data."
      });
    } finally {
      setIsLoadingOn(false);
    }
  };

    const downloadDumpData = async () => {
      setIsLoadingOn(true);
      const tempData = await getData("dashboard/getExcelItemData/nothing");
      setIsLoadingOn(false);
      if (tempData.statusCode === 200) {
        const downloadLink = document.createElement("a");
        downloadLink.href = tempData.data.Location;
        downloadLink.target = "_blank";
        downloadLink.rel = "noopener noreferrer";
        downloadLink.click();
      }
    };

    const handleFromDateChange = (date) => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
        setFromDate(formattedDate);
    };

    const handleToDateChange = (date) => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
        setToDate(formattedDate);
    };

  const handleGotoPage = (page) => {
    if (page < 0 || page >= pageCount) {
      setErrorMessage(`No data to display`);
    } else {
      setErrorMessage("");
      setPageIndex(page);
    }
  };

  const handleSort = (column) => {
    if (column.disableSortBy) return;
    let newOrderBy = "asc";
  
    if (sortBy === column.id) {
      newOrderBy = orderBy === "asc" ? "desc" : "asc";
    }
    setSortBy(column.id);
    setOrderBy(newOrderBy);
  };
  
  useEffect(() => {
    getItemData(pageIndex, pageSize, sortBy, orderBy, queryParams, filterData, fromDate, toDate, value);
  }, [pageIndex, pageSize, sortBy, orderBy, queryParams, filterData, fromDate, toDate, value]);

  const columns = useMemo(
    () => [
      {
        Header: "Sr. No.",
        accessor: (row, i) => i + 1 + pageIndex * pageSize,
        Cell: ({ cell }) => (
          <span style={{ fontWeight: "600",fontSize:"0.875rem" }}>{cell.row.index + 1 + pageIndex * pageSize}.</span>
        ),
        disableSortBy: true,
      },
      {
        Header: "Item Name",
        accessor: "item",
      },
      {
        Header: "Brand Name",
        accessor: "brandName",
      },
      {
        Header: "Product Name",
        accessor: "productName",
        Cell: ({ value }) => value || "-"
      },
      {
        Header: "Color",
        accessor: "color",
        Cell: ({ value }) => value || "-"
      },
      {
        Header: "Total Order Count",
        accessor: "total_count",
      },
      {
        Header: "Total Quantity Ordered",
        accessor: "total_quantity_ordered",
      },
      {
        Header: "Total Order Amount",
        accessor: "total_order_amount",
        Cell: ({ value }) => new Intl.NumberFormat().format(Math.floor(value))
      },
    ],
    [pageIndex, pageSize]
  );

  
    const searchManualData = async () => {
        let filterData = {};
        if (searchBoxValue) {
            filterData = {
                item: searchBoxValue,
            };
        }

        setPageIndex(0);
        const tempQueryParams = new URLSearchParams(filterData).toString();
        setQueryParams(tempQueryParams);
    };

    const resetFilters = () => {
        inputRef.current.value = "";
        setSearchBoxValue("");
        setFromDate(null);
        setToDate(null);
        setQueryParams("");
        setPageIndex(0);
        getItemData();
        setFilterData('overall');
    };

    return (
        <>
        {isLoadingOn ? <Loader /> : ""}
        <div className="customHeaderTable">
            <div className="container-fluid">
            <div className="row alignmentTable">
                <div className="col-md-2">
                    <Select 
                        defaultValue={statusOption[6]}
                        placeholder="Onboarding Status"
                        name="role"
                        options={statusOption}
                        onChange={(e) => setFilterData(e.value)}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="text"
                        className="searchInputField w-100"
                        ref={inputRef}
                        onChange={(e) => setSearchBoxValue(e.target.value)}
                        placeholder="Enter Item name"
                        value={searchBoxValue}
                    />
                </div>
                <div className="col-md-3">
                    <label>From :</label>&nbsp;
                    <DatePicker
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        selected={fromDate ? new Date(fromDate) : null}
                        onChange={handleFromDateChange}
                        placeholderText="Select Start Date"
                        maxDate={new Date()}
                    />
                </div>
                    <div className="col-md-3">
                        <label>To :</label>&nbsp;
                        <DatePicker
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                            selected={toDate ? new Date(toDate) : null}
                            onChange={handleToDateChange}
                            placeholderText="Select End Date"
                            maxDate={new Date()}
                        />
                    </div>
                {/* <div className="col-md-1">
                    <button
                        type="button"
                        className="searchTableData"
                        onClick={() => {
                            setPageIndex(0);
                            getItemData();
                        }}                >
                        <FaSearch /> Search
                    </button>
                </div> */}
                <div className="col-md-1 text-center" onClick={resetFilters}>
                  <button className="resetTableData">
                      <HiRefresh /> Reset
                  </button>
                </div>
                <div className="col-md-1 pl-0 text-center">
                  <button
                    onClick={downloadDumpData}
                    id="CSV"
                    type="button"
                    className="font-weight-bold download-csv transformBtn acceptDataCss"
                  >
                    <a href="javascript:void(0)">
                      <FaFileDownload />
                    </a>
                  </button>
                </div>
            </div>
            </div>
        </div>
        <div className="customHeaderTable">
            {records && <Table 
            columns={columns} 
            data={records} 
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
            totalRecords={totalRecords}
            handleGotoPage={handleGotoPage}
            errorMessage={errorMessage} 
            handleSort={handleSort}
            sortBy={sortBy}
            orderBy={orderBy}
            />}
        </div>
        </>
    );
};

export default ItemInnerDetails;

function Table({  
    columns, 
    data,
    pageCount,
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    totalRecords,
    handleGotoPage,
    errorMessage, 
    handleSort,
    sortBy,
    orderBy
   }) {
      const filterTypes = React.useMemo(
          () => ({
              text: (rows, id, filterValue) => {
                  return rows.filter((row) => {
                      const rowValue = row.values[id];
                      return rowValue !== undefined
                          ? String(rowValue)
                              .toLowerCase()
                              .startsWith(String(filterValue).toLowerCase())
                          : true;
                  });
              },
          }),
          []
      );
  
      const defaultColumn = React.useMemo(
          () => ({
              // Let's set up our default Filter UI
            //   Filter: DefaultColumnFilter,
          }),
          []
      );
  
      const {
          getTableProps,
          getTableBodyProps,
          headerGroups,
          footerGroups,
          rows,
          prepareRow,
          visibleColumns,
          preGlobalFilteredRows,
          setGlobalFilter,
          page,
          canPreviousPage,
          canNextPage,
          pageOptions,
          gotoPage,
          nextPage,
          previousPage,
          state: { pageIndex: tablePageIndex, pageSize: tablePageSize },
      } = useTable(
          {
              columns,
              data,
              defaultColumn, 
              filterTypes,
              manualPagination: true,
              pageCount, 
              initialState: { pageIndex, pageSize},
          },
          useFilters, // useFilters!
          useGlobalFilter, // useGlobalFilter!
          useSortBy, //sort data
          usePagination //use pagination
      );
      const handleNextPage = () => {
          if (canNextPage) {
              setPageIndex(pageIndex + 1);
          }
      };
      const handlePreviousPage = () => {
          if (canPreviousPage) {
              setPageIndex(pageIndex - 1);
          }
      };
      const handleSetPageSize = (size) => {
          setPageSize(size);
          setPageIndex(0);
      };
  
      return (
          <>
              <div>
                <table 
                    {...getTableProps()} 
                    className="dashboardTable" 
                    style={{ 
                        width: "100%", 
                        borderCollapse: "collapse", 
                        border: "1px solid #e0e0e0" ,
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                    }}
                >
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr 
                                {...headerGroup.getHeaderGroupProps()} 
                                style={{ fontSize: "0.885rem", border: "1px solid #e0e0e0", height: "40px", fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
                            >
                                {headerGroup.headers.map((column) => (
                                    <th 
                                        {...column.getHeaderProps(column.getSortByToggleProps())} 
                                        onClick={() => handleSort(column)}
                                        style={{ 
                                            border: "1px solid #e0e0e0", 
                                            padding: "8px", 
                                            textAlign: "left", 
                                            height: "40px", 
                                            verticalAlign: "middle",
                                            fontSize: "0.875rem",
                                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                                        }}
                                    >
                                        {column.render("Header")}
                                        <span>
                                            {column.id === sortBy && (orderBy === "asc" ? "↑" : "↓")}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {errorMessage ? (
                            <tr style={{ height: 150 }}>
                                <td 
                                    colSpan={columns.length} 
                                    style={{ textAlign: 'center', color: 'salmon', border: "1px solid #e0e0e0", fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
                                >
                                    <h4><strong>{errorMessage}</strong></h4>
                                </td>
                            </tr>
                        ) : (
                            page.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} style={{ border: "1px solid #e0e0e0", height: "40px" }}>
                                        {row.cells.map((cell) => (
                                            <td 
                                                {...cell.getCellProps()} 
                                                className="tableTd" 
                                                style={{ 
                                                    border: "1px solid #e0e0e0", 
                                                    padding: "8px", 
                                                    textAlign: "left", 
                                                    height: "40px", 
                                                    verticalAlign: "middle",
                                                    fontSize: "0.875rem",
                                                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>


                <div className="row m-0">
                    <div className="pagination col-lg-12">
                        <div className="col-lg-2">
                            <button
                                className="defaultPaginationButton"
                                onClick={() => handleGotoPage(0)}
                                disabled={tablePageIndex === 0}
                            >
                                {"<<"}
                            </button>{" "}
                            <button
                                className="defaultPaginationButton"
                                onClick={handlePreviousPage}
                                disabled={!canPreviousPage}
                            >
                                {"<"}
                            </button>{" "}
                            <button
                                className="defaultPaginationButton"
                                onClick={handleNextPage}
                                disabled={!canNextPage}
                            >
                                {">"}
                            </button>{" "}
                            <button
                                className="defaultPaginationButton"
                                onClick={() => handleGotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            >
                                {">>"}
                            </button>{" "}
                        </div>
                        <div className="col-lg-2">
                            <span>
                                Total Records <strong>{totalRecords}</strong>{" "}
                            </span>
                        </div>
                        <div className="col-lg-2">
                            <span>
                                Page{" "}
                                <strong>
                                {tablePageIndex + 1} of {pageCount}
                                </strong>{" "}
                            </span>
                        </div>
                        <div className="col-lg-3">
                            <span>
                                Go to page{" "}
                                <input
                                    type="number"
                                    defaultValue={tablePageIndex + 1}
                                    onChange={(e) => {
                                        const page = e.target.value
                                            ? Number(e.target.value) - 1
                                            : 0;
                                            handleGotoPage(page);
                                    }}
                                    className="gotoPage"
                                />
                            </span>{" "}
                        </div>
                        <div className="col-lg-3">
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    handleSetPageSize(Number(e.target.value))
                                }}
                                className="selectDisplayNoOfRecords"
                            >
                                {[10, 20, 30, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <br />
              </div>
          </>
      );
}